import interpolateComponents from '@automattic/interpolate-components';
import { differenceInDays, parseISO } from 'date-fns';
import { useMemo } from 'react';

import { ApiAddress } from 'api/types/ApiAddress';
import { ApiRemoteOptions } from 'api/types/ApiRemoteOptions';
import { ApiISODateTimeString } from 'api/types/ApiTypedDate';
import { Box } from 'components/Box/Box';
import { Icon } from 'components/Icon/Icon';
import { LegacyTooltip } from 'components/LegacyTooltip/LegacyTooltip';
import { Link } from 'components/Link';
import { ApiOrgType } from 'modules/listing/api/org/types/ApiOrgType';
import { ApiActionType } from 'modules/listing/api/volop/types/ApiActionType';
import { ListingTypeLabel } from 'modules/listing/components/ListingTypeLabel/ListingTypeLabel';
import { ListingViewActionButtons } from 'modules/listing/components/ListingView/ActionButtons/ListingViewActionButtons';
import { LOCATION_TYPE } from 'utils/constants/general/locationType';

import {
  ListingViewHeaderActionButtonsHolder,
  ListingViewHeaderContent,
  ListingViewHeaderContentHolder,
  ListingViewHeaderHolder,
  ListingViewHeaderLocationDescription,
  ListingViewHeaderOrgName,
  ListingViewHeaderTagHolder,
  ListingViewHeaderTitle,
} from './ListingViewHeader.styled';
import { ListingViewHeaderPublishedTag } from './ListingViewHeaderPublishedTag';
import { getListingViewHeaderLocationSentence } from './getListingViewHeaderLocationSentence';

type Props = {
  actionType?: ApiActionType;
  address: ApiAddress;
  hasAts: boolean;
  isPostedAnonymously: boolean;
  isPreview?: boolean;
  listingId: string;
  listingName: string;
  listingType: 'JOB' | 'INTERNSHIP' | 'VOLOP' | 'EVENT';
  listingUrl: Record<UserLocale, string>;
  listingFirstPublished: ApiISODateTimeString | null;
  orgId?: string;
  orgName?: string;
  orgType?: ApiOrgType;
  orgUrl?: Record<UserLocale, string> | null;
  remoteOptions: ApiRemoteOptions;
};

export function ListingViewHeader({
  actionType,
  address,
  hasAts,
  isPostedAnonymously,
  isPreview = false,
  listingId,
  listingName,
  listingType,
  listingUrl,
  listingFirstPublished,
  orgId,
  orgName,
  orgType,
  orgUrl,
  remoteOptions,
}: Props) {
  const locationSentence = useMemo(
    () =>
      getListingViewHeaderLocationSentence({
        listingType,
        remoteOptions,
        address,
      }),
    [address, listingType, remoteOptions],
  );

  const isNewListing = Boolean(
    listingFirstPublished &&
      differenceInDays(new Date(), parseISO(listingFirstPublished)) < 2,
  );

  return (
    <ListingViewHeaderHolder>
      <ListingTypeLabel
        listingType={listingType}
        orgType={orgType}
        actionType={actionType}
      />

      {listingFirstPublished && !isPreview && (
        <ListingViewHeaderTagHolder $isNew={isNewListing}>
          <ListingViewHeaderPublishedTag
            listingFirstPublished={listingFirstPublished}
            isNew={isNewListing}
          />
        </ListingViewHeaderTagHolder>
      )}

      <ListingViewHeaderContentHolder>
        <ListingViewHeaderContent>
          <ListingViewHeaderTitle data-qa-id="listing-name">
            {listingName}
            {isPostedAnonymously && (
              <Box display="inline-block" ml="6px" verticalAlign="super">
                <LegacyTooltip>
                  {getText(
                    `The hiring organization has chosen to publish this listing anonymously to maintain confidentiality during the hiring process. They have provided Idealist with all of their organization’s details and currently meet our posting guidelines.`,
                  )}
                </LegacyTooltip>
              </Box>
            )}
          </ListingViewHeaderTitle>

          {orgName && (
            <ListingViewHeaderOrgName>
              {orgUrl ? (
                <Link href={orgUrl[CURRENT_LOCALE]} data-qa-id="org-link">
                  {orgName}
                </Link>
              ) : (
                orgName
              )}
            </ListingViewHeaderOrgName>
          )}

          <ListingViewHeaderLocationDescription>
            <Icon name="location-filled" size={20} colorName="dustyBlue" />

            <div>
              <span>{LOCATION_TYPE[remoteOptions.locationType]},</span>

              <span>
                {' '}
                {locationSentence &&
                  interpolateComponents({ mixedString: locationSentence })}
              </span>
            </div>
          </ListingViewHeaderLocationDescription>

          <ListingViewHeaderActionButtonsHolder>
            <ListingViewActionButtons
              hasAts={hasAts}
              listingId={listingId}
              listingName={listingName}
              listingType={listingType}
              listingUrl={listingUrl}
              orgId={orgId}
              orgName={orgName}
              orgType={orgType}
              mobile={false}
              isPreview={isPreview}
            />
          </ListingViewHeaderActionButtonsHolder>
        </ListingViewHeaderContent>
      </ListingViewHeaderContentHolder>
    </ListingViewHeaderHolder>
  );
}
